/**
 * Dashboard Service
 * Fetches RIS admin dashboard counts (registrations today, emergency cases, pending appointment).
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiGet } from '../utils/apiClient';

export interface ModalityWorkloadItem {
  modalityId: string;
  modalityName: string;
  completed: number;
  total: number;
  /** Percentage for pie (completed/total)*100; only used when total > 0 */
  percentage: number;
  /** Raw value from API to show as-is under chart (e.g. "2/2", "3/5") */
  displayValue: string;
}

export interface FinancialSummary {
  totalCompletedAmount: number;
  pendingPayments?: number;
}

export interface RISAdminDashboardCountResponse {
  registrationsToday: number;
  emergencyCases: number;
  pendingAppointment: number;
  modalityWorkload?: ModalityWorkloadItem[];
  financialSummary?: FinancialSummary;
}

export interface DashboardStatsApiResponse {
  success: boolean;
  message?: string;
  data: RISAdminDashboardCountResponse;
}

export interface DashboardServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

function modalityWorkloadDisplayValue(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value === undefined || value === null) {
    return '0/0';
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '0/0';
}

/** Normalize API payload to camelCase for UI (handles snake_case from backend). */
function normalizeStatsData(raw: Record<string, unknown>): RISAdminDashboardCountResponse {
  const data = raw?.data as Record<string, unknown> | undefined;
  const d = data || raw;
  const num = (v: unknown): number => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);

  const modalityWorkloadRaw = d.modalityWorkload ?? d.modality_workload;
  let modalityWorkload: ModalityWorkloadItem[] = [];

  if (
    modalityWorkloadRaw &&
    typeof modalityWorkloadRaw === 'object' &&
    !Array.isArray(modalityWorkloadRaw)
  ) {
    // Format: { "CT": "2/2", "MR": "3/5", "XR": "0/0", "US": "1/2", "CT": "0/1" } – displayValue is exact API string (no calculation); percentage only for pie segment
    const entries = Object.entries(modalityWorkloadRaw);
    modalityWorkload = entries.map(([modName, value]) => {
      const displayValue = modalityWorkloadDisplayValue(value);
      const [cStr, tStr] = displayValue.split('/');
      const completed = num(cStr !== undefined ? Number(cStr) : 0);
      const total = num(tStr !== undefined ? Number(tStr) : 0);
      const percentage = total > 0 ? (completed / total) * 100 : 0;
      return {
        modalityId: modName,
        modalityName: modName,
        completed,
        total,
        percentage,
        displayValue,
      };
    });
  } else {
    const arrRaw = Array.isArray(modalityWorkloadRaw) ? modalityWorkloadRaw : [];
    modalityWorkload = arrRaw.map((m: unknown) => {
      const item = (m as Record<string, unknown>) || {};
      const completed = num(item.completed);
      const total = num(item.total);
      const displayValue =
        typeof item.displayValue === 'string'
          ? item.displayValue
          : total > 0
            ? `${completed}/${total}`
            : '0/0';
      return {
        modalityId: String(item.modalityId ?? item.modality_id ?? ''),
        modalityName: String(item.modalityName ?? item.modality_name ?? item.modality ?? ''),
        completed,
        total,
        percentage: num(item.percentage) || (total > 0 ? (completed / total) * 100 : 0),
        displayValue,
      };
    });
  }

  const financialRaw = (d.financialSummary ?? d.financial_summary) as
    | Record<string, unknown>
    | undefined;
  const financialSummary: FinancialSummary = {
    totalCompletedAmount: num(
      financialRaw?.totalCompletedAmount ?? financialRaw?.total_completed_amount
    ),
    pendingPayments: num(
      financialRaw?.pendingPayments ??
        financialRaw?.pending_payments ??
        financialRaw?.pendingPayment
    ),
  };

  return {
    registrationsToday: num(d.registrationsToday ?? d.registrations_today),
    emergencyCases: num(d.emergencyCases ?? d.emergency_cases),
    pendingAppointment: num(d.pendingAppointment ?? d.pending_appointment),
    ...(modalityWorkload.length > 0 ? { modalityWorkload } : {}),
    financialSummary,
  };
}

/**
 * Fetch RIS admin dashboard counts (Total Registrations Today, Emergency Cases, Pending Appointment).
 * Uses GET /ris/api/dashboard/stats (e.g. http://localhost:5000/ris/api/dashboard/stats).
 */
export async function getDashboardStats(
  facilityId?: string | number
): Promise<DashboardServiceResponse<RISAdminDashboardCountResponse>> {
  try {
    const url =
      facilityId != null
        ? `${API_ENDPOINTS.DASHBOARD.STATS}?facility_id=${encodeURIComponent(String(facilityId))}`
        : API_ENDPOINTS.DASHBOARD.STATS;
    const response = await apiGet<DashboardStatsApiResponse | Record<string, unknown>>(url, {
      requireAuth: true,
    });

    const apiData = response.data as DashboardStatsApiResponse | Record<string, unknown>;
    const success = (apiData as { success?: boolean })?.success;
    const payload = (apiData as { data?: Record<string, unknown> })?.data ?? apiData;

    if (payload && typeof payload === 'object' && success !== false) {
      const normalized = normalizeStatsData({ data: payload });
      return {
        success: true,
        data: normalized,
        message: (apiData as { message?: string })?.message,
      };
    }

    return {
      success: false,
      error: (apiData as { error?: string })?.error || 'Invalid response format',
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch dashboard stats';
    return {
      success: false,
      error: message,
    };
  }
}
